import * as React from 'react';

import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { useClientsQuery } from '@/lib/api/clients';
import {
  CLIENT_TYPES,
  type ClientStepState,
  type NewEngagementWizardState,
  type ProjectStepState,
} from '@/lib/wizard/new-engagement-state';
import { cn } from '@/lib/utils/cn';

/**
 * Step 1 — Client & Project (combined) pane.
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/modals-wizard.js
 * → stepClient()` (L55–115). The prototype renders client picker +
 * project fields in a single pane. We follow that contract — the v1
 * wizard previously split them across two panes for clarity; per
 * C20-131 we collapse to one to match the prototype.
 *
 * Two sub-modes for the client picker:
 *
 *   1. Existing client → typed search-select against
 *      `GET /api/v1/clients`. The user picks one of their accessible
 *      clients (firm_admin sees all; engagement_lead sees their team's).
 *   2. New client → fills name, type, contact name, contact email. The
 *      backend enum (`state_agency | county | city | other`) is the
 *      source of truth; this UI surfaces the four options verbatim.
 *
 * Project block sits below the client section (separated by a hairline
 * exactly as the prototype's `<div style="height:1px;background:var(
 * --ink-100)">`). Fields: project name (required), short code (omitted
 * — no backend column), target go-live, engagement lead, client PM
 * (optional).
 *
 * Validation: handled in `isStepValid('client-project')` — the wizard's
 * Continue button reads that gate; inline hints on email/name show only
 * after the user blurs the field.
 */
export interface StepProps {
  state: NewEngagementWizardState;
  setClient: (patch: Partial<ClientStepState>) => void;
  setNewClientField: (key: keyof ClientStepState['newClient'], value: string) => void;
  setProject: (patch: Partial<ProjectStepState>) => void;
}

export function StepClientProject({ state, setClient, setNewClientField, setProject }: StepProps) {
  const clients = useClientsQuery();
  const { client, project } = state;
  const newMode = client.mode === 'new';

  const [touched, setTouched] = React.useState<Record<string, boolean>>({});
  const markTouched = (k: string) => setTouched((t) => ({ ...t, [k]: true }));

  const emailValid =
    !touched.email ||
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.newClient.primary_contact_email) ||
    client.newClient.primary_contact_email.length === 0;
  const orgNameValid =
    !touched.orgName ||
    client.newClient.name.trim().length > 0 ||
    client.newClient.name.length === 0;
  const contactNameValid =
    !touched.contactName ||
    client.newClient.primary_contact_name.trim().length > 0 ||
    client.newClient.primary_contact_name.length === 0;
  const projectNameValid =
    !touched.projectName || project.name.trim().length > 0 || project.name.length === 0;

  return (
    <div className="space-y-4">
      {/* Client section */}
      <div>
        <label
          htmlFor="wizard-client-select"
          className="mb-1.5 block text-meta font-semibold text-ink-700"
        >
          Client
        </label>
        {clients.isLoading ? (
          <div className="flex items-center gap-2 text-body-sm text-ink-500">
            <Spinner /> Loading clients…
          </div>
        ) : clients.error ? (
          <ErrorMessage error={clients.error} />
        ) : (
          <select
            id="wizard-client-select"
            value={newMode ? '__new' : client.existingClientId}
            onChange={(ev) => {
              const v = ev.target.value;
              if (v === '__new') setClient({ mode: 'new', existingClientId: '' });
              else setClient({ mode: 'existing', existingClientId: v });
            }}
            className={inputClass(false)}
          >
            <option value="">— Select client —</option>
            {(clients.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} · {humanType(c.client_type)}
              </option>
            ))}
            <option value="__new">+ Create new client…</option>
          </select>
        )}
      </div>

      {newMode ? (
        <div className="rounded-card border border-ink-100 bg-paper-50 p-4">
          <div className="mb-3 text-h3 font-semibold text-ink-900">New client details</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Organization name"
              required
              error={!orgNameValid ? 'Required.' : undefined}
            >
              <input
                className={inputClass(!orgNameValid)}
                placeholder="e.g. City of Brightwater"
                value={client.newClient.name}
                onChange={(ev) => setNewClientField('name', ev.target.value)}
                onBlur={() => markTouched('orgName')}
              />
            </Field>
            <Field label="Type" required>
              <select
                className={inputClass(false)}
                value={client.newClient.client_type}
                onChange={(ev) => setNewClientField('client_type', ev.target.value)}
              >
                {CLIENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Primary contact"
              required
              error={!contactNameValid ? 'Required.' : undefined}
            >
              <input
                className={inputClass(!contactNameValid)}
                placeholder="Name · Title"
                value={client.newClient.primary_contact_name}
                onChange={(ev) => setNewClientField('primary_contact_name', ev.target.value)}
                onBlur={() => markTouched('contactName')}
              />
            </Field>
            <Field label="Email" required error={!emailValid ? 'Enter a valid email.' : undefined}>
              <input
                type="email"
                className={inputClass(!emailValid)}
                placeholder="name@org.gov"
                value={client.newClient.primary_contact_email}
                onChange={(ev) => setNewClientField('primary_contact_email', ev.target.value)}
                onBlur={() => markTouched('email')}
              />
            </Field>
          </div>
          <p className="mt-3 text-meta text-ink-500">
            The client is persisted when you continue from <strong>Modules</strong> — the engagement
            and (if new) client are created together so the rest of the wizard operates on live
            records.
          </p>
        </div>
      ) : null}

      {/* Hairline separator matches prototype L90 */}
      <div className="h-px bg-ink-100" />

      {/* Project section */}
      <div className="text-h3 font-semibold text-ink-900">Project</div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Project name"
          required
          error={!projectNameValid ? 'Required.' : undefined}
          span="full"
        >
          <input
            className={inputClass(!projectNameValid)}
            placeholder="e.g. ERP Modernization — Phase 2"
            value={project.name}
            onChange={(ev) => setProject({ name: ev.target.value })}
            onBlur={() => markTouched('projectName')}
          />
        </Field>
        <Field label="Target go-live" optional>
          <input
            type="date"
            className={inputClass(false)}
            value={project.planned_end}
            onChange={(ev) => setProject({ planned_end: ev.target.value })}
          />
        </Field>
        <Field label="Main POC (client)" optional span="full">
          <input
            className={inputClass(false)}
            placeholder="Name · Title"
            value={project.client_pm}
            onChange={(ev) => setProject({ client_pm: ev.target.value })}
          />
        </Field>
      </div>
    </div>
  );
}

function humanType(t: string): string {
  switch (t) {
    case 'state_agency':
      return 'State agency';
    case 'county':
      return 'County';
    case 'city':
      return 'City';
    case 'other':
      return 'Other';
    default:
      return t;
  }
}

function inputClass(error: boolean): string {
  return cn(
    'block h-input-h w-full rounded-card border bg-paper-0 px-3 text-body-sm text-ink-900',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
    error ? 'border-cite-broken' : 'border-ink-200',
  );
}

function Field({
  label,
  required,
  optional,
  error,
  span,
  children,
}: {
  label: string;
  required?: boolean;
  optional?: boolean;
  error?: string;
  span?: 'full';
  children: React.ReactNode;
}) {
  return (
    <div className={span === 'full' ? 'sm:col-span-2' : undefined}>
      <label className="mb-1 flex items-center gap-1 text-meta font-medium text-ink-700">
        <span>{label}</span>
        <span className="text-ink-500">
          {required ? '· required' : optional ? '· optional' : ''}
        </span>
      </label>
      {children}
      {error ? <div className="mt-1 text-meta text-cite-broken">{error}</div> : null}
    </div>
  );
}
